import { FC } from 'react';
import { BlueprintEntity } from '@agilelab/plugin-wb-builder-common';
import { EntityErrorResult } from '@agilelab/plugin-wb-catalog-extension-common';
import {
  WbFavoriteEntityButton,
  WbTruncatedTypographyWrapper,
} from '@agilelab/plugin-wb-platform';
import { JsonObject } from '@backstage/types';
import {
  Box,
  createStyles,
  makeStyles,
  Theme,
  useTheme,
} from '@material-ui/core';
import SettingsIcon from '@material-ui/icons/Settings';
import { Skeleton } from '@material-ui/lab';
import clsx from 'clsx';

const useStyles = (error: boolean = false) =>
  makeStyles((theme: Theme) =>
    createStyles({
      header: {
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        background: error
          ? theme.palette.softened.error
          : theme.palette.primary.main,
        gap: '16px',
      },
      headerIcon: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
      },
      headerAvatarContainer: {
        width: '64px',
        height: '64px',
        boxShadow: '0px 4px 4px 0px #00000040',
        borderRadius: '16px',
        background: theme.palette.white,
        marginBottom: '-32px',
        padding: '8px',
        flexShrink: 0,
      },
      headerAvatar: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '1.125rem',
        pointerEvents: 'none',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: 'contain',
      },
      headerStar: {
        color: theme.palette.white,
        padding: 0,
      },
      emptyIcon: {
        width: '100%',
        height: '100%',
      },
      empty: {
        background: theme.palette.grey[100],
      },
    }),
  );

interface Props {
  blueprint?: BlueprintEntity;
  error?: EntityErrorResult;
  hideFavourite?: boolean;
  loading?: boolean;
}

export const BlueprintCardHeader: FC<Props> = ({
  blueprint,
  error,
  hideFavourite,
  loading,
}) => {
  const hasError = Boolean(error);
  const theme = useTheme();
  const classes = useStyles(hasError)();
  const title = blueprint?.metadata.title || blueprint?.metadata.name;
  const icon = (blueprint?.metadata.mesh as JsonObject | undefined)
    ?.icon as string;

  return (
    <Box className={clsx(classes.header, !blueprint && classes.empty)}>
      <Box className={classes.headerIcon}>
        <Box className={classes.headerAvatarContainer}>
          {blueprint && title && (
            <Box
              className={classes.headerAvatar}
              style={{
                backgroundImage: icon ? `url(${icon})` : '',
              }}
            >
              {!icon &&
                title
                  .split(' ')
                  .slice(0, 2)
                  .map(s => s.charAt(0))}
            </Box>
          )}
          {!blueprint && <SettingsIcon className={classes.emptyIcon} />}
        </Box>
        {title && (
          <WbTruncatedTypographyWrapper
            value={title}
            maxLines={1}
            style={{
              color: hasError ? theme.palette.black : theme.palette.white,
            }}
            variant="h6"
          />
        )}
        {!blueprint && <Skeleton animation={!loading && loading} width={90} />}
      </Box>
      {!hideFavourite && blueprint && (
        <WbFavoriteEntityButton
          entity={blueprint}
          style={{ color: theme.palette.white }}
        />
      )}
    </Box>
  );
};
